import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import FashionCard from '../components/FashionCard';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandWeekPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/brand-week`);
      setData(res.data);
    } catch {
      toast.error('Failed to load Brand Week');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a]">
        <div className="py-32 text-center"><div className="skeleton h-8 w-64 mx-auto rounded" /></div>
      </div>
    );
  }

  if (!data || !data.active) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent mx-auto mb-8" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">One Brand Week</p>
          <h1 className="text-4xl md:text-5xl font-serif italic text-white mb-6">Coming Soon</h1>
          <p className="text-gray-500 max-w-md mx-auto mb-10">The next brand spotlight is being curated. Check back soon.</p>
          <Link to="/feed"><Button className="bg-white/5 text-white border border-white/10 hover:bg-white/10 px-8 py-5 text-xs uppercase tracking-[0.2em]">Explore Feed</Button></Link>
        </div>
      </div>
    );
  }

  const accent = data.accent_color;
  const brand = data.brand || {};
  const endDate = new Date(data.end_date);
  const daysLeft = Math.max(0, Math.ceil((endDate - new Date()) / 86400000));

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="brand-week-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {brand.cover_image && (
          <div className="absolute inset-0">
            <img src={brand.cover_image} alt={data.brand_name} className="w-full h-full object-cover opacity-30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/40 to-[#0a0a0a]" />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-transparent mx-auto mb-8" style={{ backgroundImage: `linear-gradient(to bottom, transparent, ${accent}60, transparent)` }} />

          <div className="inline-flex items-center gap-3 px-4 py-2 border rounded-full mb-8" style={{ borderColor: `${accent}40`, backgroundColor: `${accent}10` }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
            <span className="text-[10px] uppercase tracking-[0.25em] font-medium" style={{ color: accent }}>One Brand Week</span>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif italic text-white mb-4">{data.brand_name}</h1>
          <p className="text-xl md:text-2xl font-serif italic text-gray-400 mb-6">{data.tagline}</p>
          <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed mb-10">{data.description}</p>

          <div className="flex items-center justify-center gap-8 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar className="w-4 h-4" style={{ color: accent }} />
              <span>{daysLeft} {daysLeft === 1 ? 'day' : 'days'} remaining</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <ShoppingBag className="w-4 h-4" style={{ color: accent }} />
              <span>{data.looks.length} curated looks</span>
            </div>
          </div>

          <div className="w-px h-12 bg-gradient-to-b from-transparent to-transparent mx-auto mt-10" style={{ backgroundImage: `linear-gradient(to bottom, transparent, ${accent}60, transparent)` }} />
        </div>
      </section>

      {/* Brand Heritage */}
      {brand.description && (
        <section className="py-20 px-6 md:px-12 bg-[#111111] border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">Heritage</p>
                <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-6">The House of {data.brand_name}</h2>
                <p className="text-gray-500 leading-relaxed mb-6">{brand.description}</p>
                <div className="flex gap-8">
                  {brand.founded && (
                    <div>
                      <p className="text-2xl font-serif text-white">{brand.founded}</p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-600 mt-1">Founded</p>
                    </div>
                  )}
                  {brand.country && (
                    <div>
                      <p className="text-2xl font-serif text-white">{brand.country}</p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-600 mt-1">Origin</p>
                    </div>
                  )}
                </div>
              </div>
              {brand.cover_image && (
                <div className="aspect-square overflow-hidden border border-white/5">
                  <img src={brand.cover_image} alt={data.brand_name} className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Curated Looks */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">This Week</p>
            <h2 className="text-3xl md:text-4xl font-serif italic text-white">Curated {data.brand_name} Looks</h2>
          </div>

          {data.looks.length > 0 ? (
            <div className="bento-grid">
              {data.looks.map((look, index) => (
                <FashionCard key={look.id} look={look} featured={index === 0} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/5 bg-[#111111]">
              <p className="text-gray-500 mb-4">No looks for this brand yet.</p>
              <Link to="/create"><Button className="bg-white/5 text-white border border-white/10 hover:bg-white/10 text-xs uppercase tracking-wider">Be The First</Button></Link>
            </div>
          )}
        </div>
      </section>

      {/* Shop CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#111111] border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-serif italic text-white mb-6">Shop the Collection</h2>
          <p className="text-gray-500 mb-10">Explore {data.brand_name} directly. Every click supports Meet Mr. Fashion.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/brands/${data.brand_slug}`}>
              <Button className="text-white px-8 py-6 text-xs uppercase tracking-[0.2em]" style={{ backgroundColor: accent }}>
                View {data.brand_name} Looks
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/feed">
              <Button className="bg-white/5 text-white border border-white/10 hover:bg-white/10 px-8 py-6 text-xs uppercase tracking-[0.2em]">
                Explore Full Feed
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandWeekPage;
